<section class="delete-wizard">
  @if (!accountDeletionInProgress && !accountDeleted) {
    <header class="delete-wizard__header">
      <div class="delete-wizard__header-icon"><mat-icon>person_off</mat-icon></div>
      <div>
        <span class="delete-wizard__eyebrow">Account security</span>
        <h2>Delete account</h2>
        <p>This guided verification helps prevent accidental account deletion.</p>
      </div>
    </header>

    <nav class="delete-wizard__steps" aria-label="Account deletion progress">
      <div class="delete-wizard__step" [class.delete-wizard__step--active]="currentStep === 0"
        [class.delete-wizard__step--complete]="currentStep > 0">
        <span>@if (currentStep > 0) { <mat-icon>check</mat-icon> } @else { 1 }</span>
        <small>Review</small>
      </div>
      <div class="delete-wizard__step-line" [class.delete-wizard__step-line--complete]="currentStep > 0"></div>
      <div class="delete-wizard__step" [class.delete-wizard__step--active]="currentStep === 1"
        [class.delete-wizard__step--complete]="currentStep > 1">
        <span>@if (currentStep > 1) { <mat-icon>check</mat-icon> } @else { 2 }</span>
        <small>Identity</small>
      </div>
      <div class="delete-wizard__step-line" [class.delete-wizard__step-line--complete]="currentStep > 1"></div>
      <div class="delete-wizard__step" [class.delete-wizard__step--active]="currentStep === 2"
        [class.delete-wizard__step--complete]="currentStep > 2">
        <span>@if (currentStep > 2) { <mat-icon>check</mat-icon> } @else { 3 }</span>
        <small>Code</small>
      </div>
      <div class="delete-wizard__step-line" [class.delete-wizard__step-line--complete]="currentStep > 2"></div>
      <div class="delete-wizard__step" [class.delete-wizard__step--active]="currentStep === 3">
        <span>4</span>
        <small>Confirm</small>
      </div>
    </nav>
  }

  <main class="delete-wizard__content">
    @if (currentStep === 0 && !accountDeletionInProgress && !accountDeleted) {
      <section class="delete-wizard__stage">
        <div class="delete-wizard__stage-heading">
          <span class="delete-wizard__stage-icon"><mat-icon>warning_amber</mat-icon></span>
          <div>
            <h3>Review what will be deleted</h3>
            <p>This action permanently removes your workspace and cannot be undone.</p>
          </div>
        </div>

        <div class="delete-wizard__warning">
          <mat-icon>info</mat-icon>
          <span>Make sure you have downloaded anything you want to keep before continuing.</span>
        </div>

        <div class="delete-wizard__impact-grid">
          <div class="delete-wizard__impact"><mat-icon>person</mat-icon><span>Profile information and settings</span></div>
          <div class="delete-wizard__impact"><mat-icon>desktop_windows</mat-icon><span>Connected screens and groups</span></div>
          <div class="delete-wizard__impact"><mat-icon>perm_media</mat-icon><span>Uploaded media and content</span></div>
          <div class="delete-wizard__impact"><mat-icon>playlist_play</mat-icon><span>Playlists and schedules</span></div>
          <div class="delete-wizard__impact"><mat-icon>analytics</mat-icon><span>Account history and analytics</span></div>
          <div class="delete-wizard__impact"><mat-icon>cloud_off</mat-icon><span>Other ZignFlix account data</span></div>
        </div>

        <footer class="delete-wizard__actions">
          <button class="secondary-btn" mat-flat-button type="button" (click)="closeDialog()">Cancel</button>
          <button class="primary-btn" mat-flat-button type="button" (click)="nextStep()"
            [disabled]="isVerifyPasswordLoading">
            Continue<mat-icon iconPositionEnd>arrow_forward</mat-icon>
          </button>
        </footer>
      </section>
    }

    @if (currentStep === 1 && !accountDeletionInProgress && !accountDeleted) {
      <form class="delete-wizard__stage" [formGroup]="confirmUserPasswordForm" (ngSubmit)="verifyPassword()">
        <div class="delete-wizard__stage-heading">
          <span class="delete-wizard__stage-icon"><mat-icon>lock</mat-icon></span>
          <div>
            <h3>Confirm your identity</h3>
            <p>Enter your current password to request a secure verification code.</p>
          </div>
        </div>

        <div class="delete-wizard__account-summary">
          <mat-icon>account_circle</mat-icon>
          <div><strong>{{ confirmUserPasswordForm.get('full_name')?.value }}</strong><span>{{ getUserEmail() }}</span></div>
        </div>

        <mat-form-field appearance="outline">
          <mat-label>Current password</mat-label>
          <mat-icon matPrefix>key</mat-icon>
          <input matInput [type]="hidePassword ? 'password' : 'text'" formControlName="password"
            [attr.maxLength]="limits.name" autocomplete="current-password">
          <button type="button" mat-icon-button matSuffix (click)="hidePassword = !hidePassword"
            [attr.aria-label]="hidePassword ? 'Show password' : 'Hide password'">
            <mat-icon>{{ hidePassword ? 'visibility_off' : 'visibility' }}</mat-icon>
          </button>
          @if (confirmUserPasswordForm.get('password')?.hasError('required')) {
            <mat-error>Password is required</mat-error>
          }
          @if (confirmUserPasswordForm.get('password')?.hasError('pattern')) {
            <mat-error>Invalid password</mat-error>
          }
        </mat-form-field>

        <footer class="delete-wizard__actions">
          <button class="secondary-btn" mat-flat-button type="button" (click)="closeDialog()">Cancel</button>
          <button class="primary-btn" mat-flat-button type="submit" [disabled]="isVerifyPasswordLoading">
            <mat-icon>verified_user</mat-icon>Verify password
          </button>
        </footer>
      </form>
    }

    @if (currentStep === 2 && !accountDeletionInProgress && !accountDeleted) {
      <form class="delete-wizard__stage" [formGroup]="verifyCodeForm" (ngSubmit)="verifyCode()">
        <div class="delete-wizard__stage-heading">
          <span class="delete-wizard__stage-icon"><mat-icon>mark_email_read</mat-icon></span>
          <div>
            <h3>Enter your verification code</h3>
            <p>We sent a six-character code to <strong>{{ getUserEmail() }}</strong>.</p>
          </div>
        </div>

        <div class="delete-wizard__code-wrap">
          <mat-form-field appearance="outline">
            <mat-label>Verification code</mat-label>
            <mat-icon matPrefix>password</mat-icon>
            <input class="delete-wizard__code-input" matInput formControlName="verification_code" maxlength="6"
              pattern="[A-Za-z0-9]{0,6}" autocomplete="one-time-code">
            @if (verifyCodeForm.get('verification_code')?.hasError('required')) {
              <mat-error>Code is required</mat-error>
            }
          </mat-form-field>

          <div class="delete-wizard__resend">
            @if (isTimerRunning || (!isTimerRunning && countdown === originalCountDown)) {
              <span><mat-icon>schedule</mat-icon>Resend available in {{ getCountDownTime() }}</span>
            }
            @if (!isTimerRunning && countdown !== originalCountDown) {
              <button type="button" (click)="resendCode()"><mat-icon>refresh</mat-icon>Resend code</button>
            }
          </div>
        </div>

        <footer class="delete-wizard__actions">
          <button class="secondary-btn" mat-flat-button type="button" (click)="closeDialog()">Cancel</button>
          <button class="primary-btn" mat-flat-button type="submit">
            <mat-icon>verified</mat-icon>Verify code
          </button>
        </footer>
      </form>
    }

    @if (currentStep === 3 && !accountDeletionInProgress && !accountDeleted) {
      <section class="delete-wizard__stage">
        <div class="delete-wizard__stage-heading">
          <span class="delete-wizard__stage-icon delete-wizard__stage-icon--danger"><mat-icon>gpp_bad</mat-icon></span>
          <div>
            <h3>Final confirmation</h3>
            <p>This is your last opportunity to keep your account and workspace data.</p>
          </div>
        </div>

        <div class="delete-wizard__final-card">
          <mat-icon>delete_forever</mat-icon>
          <div>
            <strong>Account deletion is irreversible</strong>
            <p>After confirmation, deletion begins immediately and cannot be paused or cancelled.</p>
          </div>
        </div>

        <div class="delete-wizard__confirmation-note">
          <mat-icon>check_circle</mat-icon>
          <span>Your identity and email verification have been completed.</span>
        </div>

        <footer class="delete-wizard__actions">
          <button class="secondary-btn" mat-flat-button type="button" (click)="closeDialog()">Keep my account</button>
          <button class="primary-btn delete-wizard__danger-btn" mat-flat-button type="button"
            (click)="deleteAccount()">
            <mat-icon>delete_forever</mat-icon>Delete permanently
          </button>
        </footer>
      </section>
    }

    @if (accountDeletionInProgress && !accountDeleted) {
      <section class="delete-wizard__status" aria-live="polite">
        <div class="delete-wizard__status-visual delete-wizard__status-visual--progress">
          <img src="svg/acc_delete_progress.svg" alt="">
        </div>
        <span class="delete-wizard__eyebrow">Deletion in progress</span>
        <h2>Removing your account data</h2>
        <p>Please keep this window open until the process is complete.</p>

        <div class="delete-wizard__progress-card">
          <div class="delete-wizard__progress-meta"><strong>{{ deletionProgress }}%</strong><span>{{ deletionProgress < 100 ? 'In progress' : 'Completed' }}</span></div>
          <mat-progress-bar [value]="deletionProgress" class="custom-thick-progress-bar"
            [color]="accountDeletionInProgress ? 'primary' : 'accent'"></mat-progress-bar>
        </div>

        <div class="delete-wizard__message-card">
          <mat-icon>favorite_border</mat-icon>
          <div><strong>Thank you for being with us</strong><span>We respect your decision and hope to welcome you back in the future.</span></div>
        </div>
      </section>
    }

    @if (accountDeleted) {
      <section class="delete-wizard__status" aria-live="polite">
        <div class="delete-wizard__status-visual delete-wizard__status-visual--success">
          <img src="svg/acc_delete_success.svg" alt="">
        </div>
        <span class="delete-wizard__eyebrow">Process complete</span>
        <h2>Account successfully deleted</h2>
        <p>You will be redirected to the login screen in a few seconds.</p>

        <div class="delete-wizard__progress-card">
          <div class="delete-wizard__progress-meta"><strong>100%</strong><span>Completed</span></div>
          <mat-progress-bar [value]="deletionProgress" class="custom-thick-progress-bar"
            [color]="accountDeletionInProgress ? 'primary' : 'accent'"></mat-progress-bar>
        </div>

        <div class="delete-wizard__message-card">
          <mat-icon>waving_hand</mat-icon>
          <div><strong>Until we meet again</strong><span>If you return, we will be here with new features and improvements.</span></div>
        </div>
      </section>
    }
  </main>
</section>
